import { Controller } from '@hotwired/stimulus'
import { Modal } from 'bootstrap'

/* stimulusFetch: 'lazy' */
/**
 * DDT-4155: the lawyer checks a new login email, confirms with the current password, then gets a link in the
 * new inbox. Every message comes from the server (EmailChangeStatus), so the pop-up only maps codes to states.
 *
 * The address is checked when the field loses focus (or on Enter): there is no "check" button (DDT-4171).
 */
export default class extends Controller {
    static values = {
        checkUrl: String,
        requestUrl: String,
        csrfToken: String,
        checkingTitle: String,
        checkingText: String,
        errorText: String,
    }

    static targets = [
        'form',
        'emailField',
        'emailInput',
        'notice',
        'noticeTitle',
        'noticeBody',
        'passwordField',
        'passwordInput',
        'passwordHelper',
        'eyeButton',
        'eyeShow',
        'eyeHide',
        'submitButton',
        'done',
        'doneEmail',
    ]

    // Field state and notice tone per server code; any other code is an error and blocks the submission.
    static states = {
        deliverable: ['valid', 'success'],
        risky: ['warning', 'warning'],
    }

    static retryable = ['check_failed', 'too_many_requests']

    // Longer than a click on the backdrop takes to close the pop-up.
    static backdropDelay = 400

    connect() {
        this.checking = false
        this.checkedEmail = null
        this.canSubmit = false
        this.pendingCheck = null
        this.element.addEventListener('show.bs.modal', this.markBackdrop)
        this.element.addEventListener('hide.bs.modal', this.cancelPendingCheck)
        this.element.addEventListener('hidden.bs.modal', this.reset)
        this.noticeTarget.addEventListener('click', this.noticeClicked)
    }

    disconnect() {
        this.cancelPendingCheck()
        document.body.classList.remove('email-change-modal-open')
        this.element.removeEventListener('show.bs.modal', this.markBackdrop)
        this.element.removeEventListener('hide.bs.modal', this.cancelPendingCheck)
        this.element.removeEventListener('hidden.bs.modal', this.reset)
        this.noticeTarget.removeEventListener('click', this.noticeClicked)
    }

    // The design's scrim (black 60%) differs from the theme's; Bootstrap appends the backdrop to <body>, out of
    // reach of this element, so the body says which pop-up it belongs to. Set before the backdrop is created.
    markBackdrop = () => {
        document.body.classList.add('email-change-modal-open')
    }

    cancelPendingCheck = () => {
        clearTimeout(this.pendingCheck)
        this.pendingCheck = null
    }

    emailChanged() {
        // Any edit invalidates the previous check: the address is checked again when the field is left.
        this.cancelPendingCheck()
        this.checkedEmail = null
        this.canSubmit = false
        this.setEmailState(null)
        this.hideNotice()
        this.updateSubmit()
    }

    emailLeft(event) {
        // Leaving the field to close the pop-up must not spend a paid check.
        if (event.relatedTarget?.closest('[data-bs-dismiss="modal"]')) {
            return
        }

        // Focus lands on the modal itself both for a click on plain text inside it and for a click on the
        // backdrop, which closes it once the mouse is released. Wait for that: hide.bs.modal cancels the check.
        if (event.relatedTarget === this.element) {
            this.pendingCheck = setTimeout(() => this.check(), this.constructor.backdropDelay)

            return
        }

        this.check()
    }

    async check(event) {
        event?.preventDefault()
        this.cancelPendingCheck()

        const email = this.emailInputTarget.value.trim()

        // One paid check at a time, never twice for the same address, never for an empty field.
        if (this.checking || '' === email || email === this.checkedEmail) {
            return
        }

        this.checking = true
        this.checkedEmail = email
        this.canSubmit = false
        this.updateSubmit()
        this.setEmailState('checking')
        this.showNotice('checking', this.checkingTitleValue, this.checkingTextValue)

        const json = await this.post(this.checkUrlValue, { email })

        this.checking = false

        // The address was edited while the check ran: this result is for another value, drop it and check the new one.
        if (email !== this.emailInputTarget.value.trim()) {
            this.checkedEmail = null
            if (document.activeElement !== this.emailInputTarget) {
                this.check()
            }

            return
        }

        if (!json) {
            // Network failure: let the lawyer try again on the same address.
            this.checkedEmail = null
            this.setEmailState('error')

            return
        }

        // These say "try again": let the next blur or Enter re-check the same address.
        if (this.constructor.retryable.includes(json.code)) {
            this.checkedEmail = null
        }

        this.showEmailResult(json)
        this.canSubmit = json.status
        this.updateSubmit()
    }

    passwordChanged() {
        this.setPasswordError(null)
        this.updateSubmit()
    }

    togglePassword() {
        const visible = 'password' === this.passwordInputTarget.type

        this.passwordInputTarget.type = visible ? 'text' : 'password'
        this.eyeButtonTarget.setAttribute('aria-pressed', String(visible))
        this.eyeShowTarget.hidden = visible
        this.eyeHideTarget.hidden = !visible
    }

    async submit(event) {
        event?.preventDefault()

        // Enter in the password field bypasses the disabled button.
        if (this.submitButtonTarget.disabled) {
            return
        }

        this.submitButtonTarget.disabled = true

        const email = this.emailInputTarget.value.trim()
        const json = await this.post(this.requestUrlValue, {
            email,
            password: this.passwordInputTarget.value,
        })

        if (!json) {
            this.updateSubmit()

            return
        }

        if ('confirmation_sent' === json.code) {
            this.doneEmailTarget.textContent = email
            this.formTarget.hidden = true
            this.doneTarget.hidden = false

            return
        }

        if ('wrong_password' === json.code) {
            // The checked address stays valid: only the password is wrong.
            this.setPasswordError(json.message)
            this.updateSubmit()

            return
        }

        // The address was re-checked on submission and refused (taken meanwhile, rate limit…): it needs a new check.
        this.showEmailResult(json)
        this.canSubmit = json.status
        this.updateSubmit()
    }

    noticeClicked = (event) => {
        // "Contact us" in the "already used" notice: hand over to the customer service pop-up.
        if (!event.target.closest('[data-email-change-contact]')) {
            return
        }

        event.preventDefault()

        const contactModal = document.getElementById('contact-customer-service-modal')

        this.element.addEventListener('hidden.bs.modal', () => Modal.getOrCreateInstance(contactModal).show(), { once: true })
        Modal.getOrCreateInstance(this.element).hide()
    }

    reset = () => {
        document.body.classList.remove('email-change-modal-open')
        this.checking = false
        this.checkedEmail = null
        this.canSubmit = false
        this.emailInputTarget.value = ''
        this.passwordInputTarget.value = ''
        if ('text' === this.passwordInputTarget.type) {
            this.togglePassword()
        }
        this.setEmailState(null)
        this.setPasswordError(null)
        this.hideNotice()
        this.updateSubmit()
        this.formTarget.hidden = false
        this.doneTarget.hidden = true
    }

    async post(url, data) {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-Token': this.csrfTokenValue,
                },
                body: JSON.stringify(data),
            })

            return await response.json()
        } catch (error) {
            console.error(error)
            this.showNotice('error', '', this.errorTextValue)

            return null
        }
    }

    showEmailResult(json) {
        const [state, tone] = this.constructor.states[json.code] ?? ['error', 'error']

        this.setEmailState(state)
        // A CSRF refusal carries no message.
        this.showNotice(tone, json.title ?? '', json.message || this.errorTextValue)
    }

    setEmailState(state) {
        if (state) {
            this.emailFieldTarget.dataset.state = state
        } else {
            delete this.emailFieldTarget.dataset.state
        }
    }

    setPasswordError(message) {
        if (message) {
            this.passwordFieldTarget.dataset.state = 'error'
            this.passwordHelperTarget.textContent = message
        } else {
            delete this.passwordFieldTarget.dataset.state
            this.passwordHelperTarget.textContent = this.passwordHelperTarget.dataset.defaultText
        }
    }

    // The texts are the server's own translations (EmailChangeController): they may hold <strong> and the
    // "contact us" link, and never any user input.
    showNotice(tone, title, body) {
        this.noticeTarget.dataset.tone = tone
        this.noticeTitleTarget.textContent = title
        this.noticeBodyTarget.innerHTML = body
        this.noticeTarget.hidden = false
    }

    hideNotice() {
        this.noticeTarget.hidden = true
        delete this.noticeTarget.dataset.tone
        this.noticeTitleTarget.textContent = ''
        this.noticeBodyTarget.textContent = ''
    }

    updateSubmit() {
        this.submitButtonTarget.disabled = this.checking || !this.canSubmit || '' === this.passwordInputTarget.value
    }
}
